import React from "react";
import { Eye, SquarePen, Trash2 } from "lucide-react";
import { ProjectViewRecord } from "../../store/apis/resource-allocation/project-management.api";
import { formatLocalDate } from "../../utils/timeUtils";
import { getAvatarSolid } from "../../utils/avatarUtils";
import { getInitials, RowActionsMenu, MenuItem } from "../common/Table2/cells";
import {
  getProjectProgress,
  getProjectHealth,
} from "../../utils/projectProgress";

interface ProjectCardsGridProps {
  projects: ProjectViewRecord[];
  canView?: boolean;
  canManage?: boolean;
  onView: (p: ProjectViewRecord) => void;
  onEdit: (p: ProjectViewRecord) => void;
  onDelete: (p: ProjectViewRecord) => void;
}

const ProjectCardsGrid: React.FC<ProjectCardsGridProps> = ({
  projects,
  canView = true,
  canManage = false,
  onView,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
      {projects.map((p) => {
        const health = getProjectHealth(p.status, p.endDate);
        const progress = getProjectProgress(p.startDate, p.endDate);
        const avatarColor = getAvatarSolid(p.name);
        const teamCount = p.allocatedResources?.length ?? 0;

        const items: MenuItem[] = [];
        if (canView) {
          items.push({
            key: "view",
            label: "View",
            icon: <Eye className="w-4 h-4" />,
            onClick: () => onView(p),
          });
        }
        if (canManage) {
          items.push({
            key: "edit",
            label: "Edit",
            icon: <SquarePen className="w-4 h-4" />,
            onClick: () => onEdit(p),
          });
          items.push({
            key: "delete",
            label: "Delete",
            icon: <Trash2 className="w-4 h-4" />,
            danger: true,
            dividerBefore: true,
            onClick: () => onDelete(p),
          });
        }

        return (
          <div
            key={p.id}
            className="bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] rounded-[18px] p-4 sm:p-5 shadow-[var(--shadow-sm)] transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
          >
            <div className="flex items-center gap-[13px]">
              <div
                className="w-[46px] h-[46px] rounded-[13px] flex-none grid place-items-center text-white font-extrabold text-[14px]"
                style={{ background: avatarColor }}
              >
                {getInitials(p.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-bold text-[var(--color-text-primary)] truncate">
                  {p.name}
                </div>
                <div className="text-[12px] text-[var(--color-text-tertiary)] truncate mt-[2px]">
                  {p.clientName || "—"} · {teamCount}{" "}
                  {teamCount === 1 ? "person" : "people"} · due{" "}
                  {formatLocalDate(p.endDate)}
                </div>
              </div>
              <span
                className="inline-flex items-center px-[11px] py-[4px] rounded-full text-[11.5px] font-bold flex-none"
                style={{ background: health.bg, color: health.color }}
              >
                {health.label}
              </span>
            </div>

            <div className="flex items-center justify-between mt-[18px] mb-[7px]">
              <span className="text-[12px] font-semibold text-[var(--color-text-secondary)]">
                Progress
              </span>
              <div className="flex items-center gap-1.5">
                <span className="text-[12.5px] font-bold text-[var(--color-text-primary)]">
                  {progress}%
                </span>
                {items.length > 0 && <RowActionsMenu items={items} />}
              </div>
            </div>

            <div className="h-2 rounded-full bg-[var(--color-bg-track)] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${progress}%`, background: avatarColor }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectCardsGrid;